import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { KeyRound, MailCheck } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LangSwitcher } from "../components/LangSwitcher";
import { usePrivateSeo } from "../lib/useSeo";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-background">
    <div className="vy-hero px-6 py-6">
      <div className="mx-auto flex max-w-[520px] items-center">
        <Link to="/" className="font-display text-xl font-bold text-white">
          Vytali<span className="text-accent">Days</span>
        </Link>
        <span className="ml-auto"><LangSwitcher dark /></span>
      </div>
    </div>
    <div className="mx-auto max-w-[520px] p-4">{children}</div>
  </div>
);

export function ForgotPassword() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  usePrivateSeo(t("account.forgotTitle"));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/password-reset/request", { email, language: i18n.language });
      setSent(true);
    } catch {
      setSent(true); // never reveal whether the address exists
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      {sent ? (
        <div className="vy-card p-6 text-center" data-testid="forgot-password-sent">
          <MailCheck className="mx-auto h-10 w-10 text-[hsl(var(--success))]" />
          <p className="mt-4 font-display text-lg font-bold">{t("account.resetSent")}</p>
          <Link to="/login" data-testid="forgot-back-login"
            className="mt-4 inline-block text-sm font-bold text-primary">{t("auth.signIn")} →</Link>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="forgot-password-form" className="vy-card p-6">
          <KeyRound className="h-8 w-8 text-primary" />
          <h1 className="mt-3 font-display text-2xl font-bold">{t("account.forgotTitle")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("account.forgotLead")}</p>
          <label className="mt-6 block text-sm font-semibold">{t("auth.email")}</label>
          <Input data-testid="forgot-email-input" type="email" required value={email}
            autoComplete="email" onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-12" />
          <Button data-testid="forgot-submit-btn" type="submit" disabled={busy}
            className="mt-6 h-14 w-full rounded-full text-base font-bold">
            {busy ? t("common.sending") : t("account.sendResetLink")}
          </Button>
          <Link to="/login" data-testid="forgot-login-link"
            className="mt-4 block text-center text-sm font-semibold text-muted-foreground">
            {t("auth.signIn")}
          </Link>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const { token } = useParams();
  const { t } = useTranslation();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [valid, setValid] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  usePrivateSeo(t("account.resetTitle"));

  useEffect(() => {
    api.get(`/auth/password-reset/${token}`)
      .then(({ data }) => { setEmail(data.email); setValid(true); })
      .catch(() => setValid(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError(t("account.tooShort")); return; }
    if (password !== confirm) { setError(t("account.mismatch")); return; }
    setBusy(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, password });
      const u = await refresh();
      toast.success(t("account.changed"));
      navigate(u && u.role !== "ambassador" ? "/staff" : "/", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      {valid === null && <p className="p-6 text-sm text-muted-foreground">{t("common.loading")}</p>}
      {valid === false && (
        <div className="vy-card p-6 text-center" data-testid="reset-invalid">
          <p className="font-display text-lg font-bold text-destructive">{t("account.resetInvalid")}</p>
          <Link to="/forgot-password" data-testid="reset-retry-link"
            className="mt-4 inline-block text-sm font-bold text-primary">{t("account.forgotTitle")} →</Link>
        </div>
      )}
      {valid && (
        <form onSubmit={submit} data-testid="reset-password-form" className="vy-card p-6">
          <h1 className="font-display text-2xl font-bold">{t("account.resetTitle")}</h1>
          <p className="mt-1 text-sm text-muted-foreground" data-testid="reset-email">{email}</p>
          <div className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-semibold">{t("account.newPassword")}</label>
              <Input data-testid="reset-password-input" type="password" required value={password}
                autoComplete="new-password"
                onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-12" />
              <p className="mt-1 text-xs text-muted-foreground">{t("auth.passwordHint")}</p>
            </div>
            <div>
              <label className="text-sm font-semibold">{t("account.confirmPassword")}</label>
              <Input data-testid="reset-confirm-input" type="password" required value={confirm}
                autoComplete="new-password"
                onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 h-12" />
            </div>
          </div>
          {error && (
            <p data-testid="reset-password-error"
              className="mt-4 rounded-xl bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">
              {error}
            </p>
          )}
          <Button data-testid="reset-submit-btn" type="submit" disabled={busy}
            className="mt-6 h-14 w-full rounded-full text-base font-bold">
            {busy ? t("common.loading") : t("account.savePassword")}
          </Button>
        </form>
      )}
    </Shell>
  );
}
